/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Grid, Typography, IconButton, gridClasses } from '@mui/material';
import ModalDialog from '../ui/ModalDialog/index';
import { CancelPresentationRounded, ContentCopyOutlined, DeleteRounded, EditOutlined, SaveRounded, FileDownload, FileUpload } from '@mui/icons-material';
import PropTypes from 'prop-types';
import Datagrid from '../ui/Datagrid';
import columns from './table_config';
import { csvFileToArray } from '../../utils/csvFileToArray';
import styles from './style';
import { addNewDrillStringRow, copyDrillStringRow, createDrillString, deleteDrillStringRow, importDrillStringDetails, listDrillString, updateDrillString } from '../../store/actions/wellsDrillString';
import DrillStringDetails from './drillStringDetails';
import { enqueueSnackbar } from '../../store/actions/snackbar';
import SelectEditInputTextField from '../ui/SelectEditInputTextField';
import DrillStringBitDetails from './drillStringBit';
import DeleteConfirmationModal from '../deleteConfirmationModal';
import useUnsavedChanges from '../../hooks/useUnsavedChanges';
import useUnitConversion from '../../hooks/useUnitConversion';
import { UNIT_SYSTEM } from '../../shared/config/hydraulics_constants';
import DrillStringConfirmationModal from './drillStringImportConfirmationModal';
import { removeKeysFromObject } from '../../utils/objectHelpers';
import { isFullArray, isFullObject } from '../../utils/validators';
import { checkPermissions } from '../../utils/checkPermissions';
import { APP_ACTION_TYPES } from '../../utils/constants';
const DrillStringModal = ({
  onCloseClick,
  drillStringData,
  isOpen
}) => {
  const dispatch = useDispatch();
  const classes = styles();
  const drillString = useSelector((state) => state.wellsDrillString);
  const apiRef = useGridApiRef();
  const fileReader = new FileReader();
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [showBHADialog, setShowBHADialog] = useState({
    open: false,
    data: null
  });
  const [showBITDialog, setShowBITDialog] = useState({
    open: false,
    data: null
  });
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const [importConfirmAction, setImportConfirmAction] = useState(false);
  const [importRow, setImportRow] = useState();
  const { unit, convertUnitSystem } = useUnitConversion();
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, onCloseClick);

  useEffect(() => {
    if (!drillString.loading) {
      dispatch(listDrillString(drillStringData?.row?._id, onCloseClick));
    }
  }, []);

  useEffect(() => {
    if (drillString?.data) {
      setProperties(drillString?.data);
    }
  }, [drillString?.data]);

  useEffect(() => {
    if (properties[0]?.name === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'name', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'name');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const handleAddDrillString = () => {
    dispatch(addNewDrillStringRow());
  };

  const closeHandler = () => {
    if (unSavedExists) {
      handleClose(false);
      return;
    }
    onCloseClick();
  };

  const getModalTitle = () => {
    return (
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          {drillStringData?.row?.name || ''} | Drillstring
        </Grid>
        <Grid item>
          <IconButton
            onClick={closeHandler}
          >
            <CancelPresentationRounded fontSize='large' />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  const removeSurvey = (row) => {
    if (row?.isNewRow) {
      const updatedDrillString = properties.filter(item => item._id !== row?._id);
      setProperties(updatedDrillString);
      dispatch(listDrillString(drillStringData?.row?._id));
    } else {
      dispatch(deleteDrillStringRow(row?._id, drillStringData?.row?._id));
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const hasDuplicateMDAndName = (arr, row) => {
    const seen = new Map();
    for (const item of arr) {
      const key = `${Number(item.MD)}-${item.name?.toLowerCase()?.trim()}`;
      if (seen.has(key)) {
        dispatch(
          enqueueSnackbar(
            'Name and MD already exist',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        return true;
      }
      seen.set(key, true);
    }
    return false;
  };

  const checkDrillStringValues = (value) => {
    const row = value;
    const invalidFields = [];
    if (!row?.name) invalidFields.push('Name');
    if (row?.MD === undefined || row?.MD === null || row?.MD === '') invalidFields.push('MD');
    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return true;
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const handleSave = (row) => {
    if (checkDrillStringValues(row)) {
      if (!hasDuplicateMDAndName(properties, row)) {
        const convertedMD = unit !== UNIT_SYSTEM.US
          ? convertUnitSystem(row?.MD, drillString?.units?.MD.conversionFactor)
          : Number(row?.MD);
        const body = {
          ...row,
          MD: convertedMD
        };
        setInvalidRows(prev => ({ ...prev, [row._id]: false }));
        if (row?.isNewRow) {
          dispatch(createDrillString(body, drillStringData?.row?._id));
        } else {
          dispatch(updateDrillString({ ...row, MD: convertedMD, wellsInfoId: drillStringData?.row?._id }));
        }
      }
    }
  };

  const downloadRowAsJSON = async (rowData, fileName = 'data.json') => {
    try {
      if (!isFullArray(rowData?.drillstring_details) || !isFullObject(rowData?.bit_details)) {
        dispatch(
          enqueueSnackbar(
            'Please Fill Drillstring Details',
            'error',
            new Date().getTime() + Math.random()
          )
        );
        return;
      }
      const keysToIgnore = ['grid_id', 'lastUpdate', 'name', 'MD', 'wellsInfoId', 'permissionId', 'permissions'];
      const payload = removeKeysFromObject(rowData, keysToIgnore);
      const jsonString = JSON.stringify(payload, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });

      if (window.showSaveFilePicker) {
        const handle = await window.showSaveFilePicker({
          suggestedName: fileName,
          types: [
            {
              description: 'JSON File',
              accept: { 'application/json': ['.json'] },
            },
          ],
        });

        const writableStream = await handle.createWritable();
        await writableStream.write(blob);
        await writableStream.close();
      } else {
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      dispatch(
        enqueueSnackbar(
          'Failed to save JSON File',
          'error',
          new Date().getTime() + Math.random()
        )
      );
    }
  };

  const renderActionsCell = ({ row }) => {
    const hasDirtyRow = properties.some(item => item?.isDirty === true || item?.isNewRow);
    return (
      <Grid container justifyContent="center">
         <IconButton
          variant="solid"
          disabled={drillString?.loading || row?.MD === '' || hasDirtyRow || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.VIEW, row?.permissions))}
          onClick={() => {
            setShowBHADialog({
              open: true,
              data: row
            });
          }}
        >
          <EditOutlined />
        </IconButton>
        <IconButton
          disabled={!row.isDirty || drillString.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, row?.permissions))}
          variant="solid"
          onClick={() => {
            handleSave(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={properties.some(item => item?.isDirty === true || item?.isNewRow) || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.CREATE, row?.permissions))}
          onClick={() => {
            dispatch(copyDrillStringRow(row));
          }}
        >
          <ContentCopyOutlined />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={drillString.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.DELETE, row?.permissions))}
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={drillString?.loading || row?.isNewRow || row.isDirty || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.DOWNLOAD, row?.permissions))}
          onClick={() => {
            const fileName = `${drillStringData?.row?.name}--${row.name}@${row.MD}${drillString?.units?.MD.label}`;
            downloadRowAsJSON(row, fileName);
          }}
        >
          <FileDownload />
        </IconButton>
        <IconButton
          variant="solid"
          disabled={drillString?.loading || row?.isNewRow || row.isDirty || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.IMPORT, row?.permissions))}
          onClick={() => {
            setImportRow(row);
            setImportConfirmAction(true);
          }}
        >
          <FileUpload />
        </IconButton>
      </Grid>
    );
  };

  const uploadCSVFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      fileReader.onload = function (event) {
        const csvOutput = event.target.result;
        const drillStringList = csvFileToArray(csvOutput);
        dispatch(importDrillStringDetails(drillStringList));
      };
      fileReader.readAsText(file);
    }
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: value,
            isDirty
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  const renderValueTextCell = (params) => {
    return <SelectEditInputTextField {...params} updateData={updateData} />;
  };

  if (apiRef.current === null) apiRef.current = {};
  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={closeHandler}
      dialogClass={classes.dialogModal}
    >
      <Grid container>
        <Grid item className={classes.customTool} xs={1} justifyContent="space-between" sx={{ display: !checkPermissions(APP_ACTION_TYPES.CREATE, drillString?.permissions) && 'none !important' }}>
          <Grid item>
            <Typography variant='button' fontWeight='700' className={drillString.loading || properties.some(item => item?.isDirty === true || item?.isNewRow) ? classes.disableIcon : ''} onClick={handleAddDrillString}>+ Add</Typography>
          </Grid>
          <Grid item>
            <label className={classes.importLabel} htmlFor='getCSVFile'>Import</label>
            <input type='file' id='getCSVFile' accept={'.csv'} style={{ display: 'none' }} onChange={uploadCSVFile} />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Datagrid
            apiRef={apiRef}
            data={properties}
            loading={drillString.loading}
            columns={columns(renderActionsCell, renderValueCell, renderValueTextCell, drillString.units, unit)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            getRowClassName={(params) => {
              const isInvalid = invalidRows[params.id];
              const isEditable = params?.row?.permissions?.update;
              return `${isInvalid ? classes.invalidRow : ''} ${!isEditable && !params?.row?.isNewRow ? classes.rowViewMode : ''}`;
            }}
            autoHeight={false}
            getRowId={(row) => row._id}
            getRowHeight={() => 'auto'}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeaderTitle': {
                fontSize: '1.2rem',
                fontWeight: '900 !important',
              },
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
            }}
            autoheight
            editMode="cell"
            height={'50vh'}
            pageSize={100}
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            enableDirtyLock
          />
        </Grid>

        {showBHADialog.open &&
          <DrillStringDetails
            drillStringData={drillStringData}
            open={showBHADialog.open}
            handleClose={() => {
              setShowBHADialog({
                open: false,
                data: null
              });
            }}
            data={showBHADialog.data}
          />}
        {showBITDialog.open &&
          <DrillStringBitDetails
            drillStringData={drillStringData}
            open={showBITDialog.open}
            handleClose={() => {
              setShowBITDialog({
                open: false,
                data: null
              });
            }}
            data={showBITDialog.data}
          />}
        {
          deleteAction && <DeleteConfirmationModal
            open={deleteAction}
            handleDelete={removeSurvey}
            handleClose={setDeleteAction}
            deleteRow={deleteRow}
          />
        }
        {
          importConfirmAction && <DrillStringConfirmationModal
            open={importConfirmAction}
            handleClose={setImportConfirmAction}
            importRow={importRow}
          />
        }
        <UnsavedChangesModal />
      </Grid>
    </ModalDialog>
  );
};
DrillStringModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  drillStringData: PropTypes.object,
  isOpen: PropTypes.bool.isRequired
};
DrillStringModal.defaultProps = {
  drillStringData: {}
};
export default DrillStringModal;
